import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAdminNorthStarSnapshot, type NorthStarSnapshot } from "@/lib/admin-north-star.functions";
import { authedHeaders } from "@/lib/authed-fetch";

export const Route = createFileRoute("/admin/north-star")({
  head: () => ({
    meta: [
      { title: "North Star · Shugert Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: NorthStarPage,
});

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function dateTime(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function NorthStarPage() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["admin.north-star.snapshot"],
    queryFn: async () =>
      (await getAdminNorthStarSnapshot({
        headers: await authedHeaders(),
      })) as NorthStarSnapshot,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const openOpportunity = (opportunityId: string | null) => {
    if (!opportunityId) return;
    navigate({
      to: "/admin/opportunity/$opportunityId",
      params: { opportunityId },
    });
  };

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 animate-pulse rounded bg-card" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-28 animate-pulse rounded-2xl bg-card" />
          ))}
        </div>
      </div>
    );
  }

  const snapshot = query.data;
  const invalidShape =
    !!snapshot && !Array.isArray((snapshot as Partial<NorthStarSnapshot>).paid_product_revenue_mtd);

  if (query.isError || !snapshot || invalidShape) {
    return (
      <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6">
        <h1 className="font-display text-xl font-semibold text-foreground">North Star unavailable</h1>
        <p className="mt-2 text-sm text-destructive">
          {query.error?.message ??
            (invalidShape
              ? "The metrics snapshot came back in an unexpected shape (likely an expired admin session)."
              : "The metrics snapshot could not be loaded.")}
        </p>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="mt-4 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background"
        >
          Retry
        </button>
      </div>
    );
  }

  const data = snapshot;
  const revenueLabel =
    data.paid_product_revenue_mtd.length === 0
      ? ", "
      : data.paid_product_revenue_mtd
          .map((item) => money(item.amount_cents, item.currency))
          .join(" · ");

  return (
    <div className="space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            Revenue operations
          </p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">
            Shugert North Star
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Target metric: monthly gross profit from qualified Shopify engagements with a measurable
            result agreed before delivery. The current cards are honest proxies from existing tables;
            missing data is shown explicitly rather than estimated.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => query.refetch()}
            className="rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground hover:bg-card"
          >
            Refresh
          </button>
          <Link
            to="/admin/dashboard"
            className="rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground hover:bg-card"
          >
            GEO operations →
          </Link>
        </div>
      </header>

      <section>
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="font-display text-xl font-semibold text-foreground">Demand and pipeline proxies</h2>
          <span className="text-xs text-muted-foreground">Last 30 days unless noted</span>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Scope requests"
            value={String(data.requests_30d)}
            detail="Submitted through the quote flow"
          />
          <MetricCard
            label="Qualified ICP proxy"
            value={String(data.qualified_proxy_30d)}
            detail="Shopify + reported revenue of $50K/month or more"
            emphasis={data.qualified_proxy_30d > 0}
          />
          <MetricCard
            label="Monthly-support interest"
            value={String(data.monthly_support_requests_30d)}
            detail="Requests mapped to monthly support"
          />
          <MetricCard
            label="Open requests"
            value={String(data.open_requests)}
            detail="Not currently marked won or lost"
          />
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-semibold text-foreground">Delivery and product revenue</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Paid GEO revenue MTD"
            value={revenueLabel}
            detail="Grouped by currency; internal audits excluded"
          />
          <MetricCard
            label="Completed deliveries"
            value={String(data.completed_deliveries_30d)}
            detail="Uses the new completed_at timestamp"
          />
          <MetricCard
            label="Average delivery time"
            value={
              data.average_delivery_hours_30d == null
                ? ", "
                : `${data.average_delivery_hours_30d}h`
            }
            detail="Payment to completion for delivered orders"
          />
          <MetricCard
            label="On-time delivery"
            value={
              data.on_time_delivery_rate_30d == null
                ? ", "
                : `${data.on_time_delivery_rate_30d}%`
            }
            detail="Completed on or before the stored ETA"
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.35fr,0.65fr]">
        <div className="overflow-hidden rounded-2xl border border-border bg-card/30">
          <div className="border-b border-border px-5 py-4">
            <h2 className="font-display text-lg font-semibold text-foreground">Recent scope requests</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Click any linked row to open the matching Opportunity and client email workspace.
            </p>
          </div>
          {data.recent_requests.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No scope requests recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b border-border bg-background/50 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Contact</th>
                    <th className="px-4 py-3 text-left font-medium">Store</th>
                    <th className="px-4 py-3 text-left font-medium">Business</th>
                    <th className="px-4 py-3 text-left font-medium">Intent</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Created</th>
                    <th className="px-4 py-3 text-right font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_requests.map((request) => (
                    <tr
                      key={request.id}
                      tabIndex={request.opportunity_id ? 0 : -1}
                      role={request.opportunity_id ? "link" : undefined}
                      aria-label={request.opportunity_id ? `Open opportunity for ${request.company || request.full_name}` : undefined}
                      onClick={() => openOpportunity(request.opportunity_id)}
                      onKeyDown={(event) => {
                        if (!request.opportunity_id || !["Enter", " "].includes(event.key)) return;
                        event.preventDefault();
                        openOpportunity(request.opportunity_id);
                      }}
                      className={`border-b border-border/60 align-top ${request.opportunity_id ? "cursor-pointer transition-colors hover:bg-card/70 focus:bg-card/70 focus:outline-none" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{request.full_name}</p>
                        <a
                          href={`mailto:${request.email}`}
                          onClick={(event) => event.stopPropagation()}
                          className="text-xs text-muted-foreground hover:underline"
                        >
                          {request.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <p>{request.company || ", "}</p>
                        <p className="mt-1 max-w-48 truncate text-xs">{request.website || "No URL"}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <p>{request.monthly_revenue || "Revenue not provided"}</p>
                        <p className="mt-1 text-xs">{request.platform || "Platform not provided"}</p>
                        {request.qualified_proxy && (
                          <span className="mt-2 inline-block rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                            ICP proxy
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <p>{request.engagement_type || ", "}</p>
                        <p className="mt-1 max-w-48 truncate text-xs">
                          {request.services.length ? request.services.join(", ") : "No service selected"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full border border-border px-2.5 py-1 text-xs text-foreground">
                          {request.status || "new"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {dateTime(request.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs font-semibold text-[#1f1f1f]">
                        {request.opportunity_id ? "Open →" : ", "}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="rounded-2xl border border-[rgba(15,118,110,0.24)] bg-[rgba(15,118,110,0.045)] p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1f1f1f]">
            Tracking gaps
          </p>
          <h2 className="mt-2 font-display text-xl font-semibold text-foreground">
            What must exist before this becomes the true North Star dashboard
          </h2>
          <ul className="mt-5 space-y-5">
            {data.tracking_gaps.map((gap) => (
              <li key={gap.key}>
                <p className="text-sm font-semibold text-foreground">{gap.label}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{gap.reason}</p>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      <footer className="text-xs text-muted-foreground">
        Snapshot generated {dateTime(data.generated_at)}. Won requests currently recorded in the last
        30 days: {data.won_requests_30d}.
      </footer>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  emphasis,
}: {
  label: string;
  value: string;
  detail: string;
  emphasis?: boolean;
}) {
  return (
    <article
      className={`rounded-2xl border p-5 ${
        emphasis ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card/30"
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">{value}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{detail}</p>
    </article>
  );
}
